// Lyra(궁수) vfx 큐 이름 → 모듈 조합 + 화살 연출(c022 D4a, PRD 3A.3·COMBAT-FEEL 2.10 계약). Kael 표(cueMap.ts)와 같은 형식에
// 화살 낙하(화살비) 묶음을 더했다. 실제 투사체(Ev.Arrow)는 게임이 쏘고 ArrowRenderer가 그린다 — 이 표의 화살은 하늘에서 떨어지는 연출 화살뿐이다(두 번 그리지 않는다).
// 정본 큐 이름은 c022 공통 계약(lyra_*). 레인 A의 src/shared/skills/lyra.ts가 같은 이름을 cues[]에 적는다.
// 색 언어: 청록 바람 #3fd0c0 + 은 #c8d2dc(PRD 3절). 바닥 고리 바탕은 어두운 청록(가산 혼합에서 바닥을 하얗게 띄우지 않는다).
// 섬광: 화면 효과는 세기 0.12 이하 비네트만(섬광으로 세지 않는다, screen.ts SOFT_PULSE). 각성 섬광은 기존 각성 흐름(awaken.ts)이 맡는다.
// 위치: 큐는 영웅 위치(x,z)와 facing으로 띄운다. reach(m)가 있는 큐(화살비 표식·낙하·폭발·덫·천궁)는 중심이 영웅 앞 reach m다.
//   게임이 정확한 중심을 아는 경우 LyraFx.playCue(..., ahead)로 덮어쓴다(예: 덫을 놓은 자리, 화살이 터진 자리 = ahead 0).
import type { CueEntry, CueLayer } from './cueMap';
import type { ModuleKind } from './types';

export const LYRA_TEAL = 0x3fd0c0;
export const LYRA_SILVER = 0xc8d2dc;
/** 은빛 심(가장 밝은 핵) */
const PALE = 0xeef7f8;
/** 어두운 청록(바닥 고리·장판 바탕) */
const DEEP = 0x0d5f58;
const T = LYRA_TEAL, S = LYRA_SILVER;

/**
 * 하늘에서 떨어지는 연출 화살 묶음(ArrowRenderer.rain). 중심(큐 위치 + reach) 둘레 radius m 원 안에 count발이
 * 큐 순간 + delay부터 spread초 동안 고르게 '착탄'한다(낙하 중간부터 띄워 착탄 시각을 판정 틱에 맞춘다).
 */
export interface ArrowRainSpec {
  readonly count: number;
  readonly radius: number;
  readonly delay: number;
  readonly spread: number;
  /** 떨어지기 시작하는 높이(m) */
  readonly height: number;
  /** 수직에서 기운 각(rad, facing 방향으로) */
  readonly tilt: number;
}

export interface LyraCueEntry extends CueEntry {
  /** 표식·착탄 중심을 영웅 앞으로 옮기는 기본 거리(m). 0 = 영웅 자리 */
  readonly reach: number;
  readonly rain?: ArrowRainSpec;
}

/** Lyra 스킬 번호(이 표 안의 묶음 번호: cutLyraSkill이 쓴다). 레인 A의 스킬 id와 다르면 LYRA_SKILL_OF로 잇는다 */
export const LyraSkill = {
  Basic: 0, Triple: 1, Backstep: 2, Pierce: 3, LaunchShot: 4, WindRain: 5, AirVolley: 6, NetTrap: 7, Explode: 8,
  Vortex: 9, Homing: 10, Skyfall: 11, Awaken: 12,
} as const;

const L = (module: ModuleKind, delay: number, preset: CueLayer['preset']): CueLayer => ({ module, delay, preset });

export const LYRA_CUE_MAP: readonly LyraCueEntry[] = [
  // ── 기본 공격(원거리 3타) ────────────────────────
  {
    cue: 'lyra_shot', skillId: LyraSkill.Basic, live: true, reach: 0,
    note: '기본 화살 발사(1·2타): 활 높이에서 앞으로 짧게 터지는 바람 결 한 줄 + 발밑 작은 바람 고리. 화살 몸통·빛 꼬리는 ArrowRenderer(Ev.Arrow kind 0).',
    layers: [
      L('trail', 0, { variant: 2, y: 1.25, ahead: 0.3, length: 1.4, width: 0.12, life: 0.13, tail: 0.09, color: T, color2: PALE, intensity: 1.8, seed: 0.2 }),
      L('ring', 0, { length: 0.75, width: 0.16, life: 0.2, from: 0.3, color: DEEP, color2: T, intensity: 0.8, seed: 0.4 }),
    ],
  },
  {
    cue: 'lyra_shot_heavy', skillId: LyraSkill.Basic, live: true, reach: 0,
    note: '3타 관통 강사: 화살을 감싸는 굵은 바람 나선(넓은 청록 투사체 심 + 엇갈린 두 결) + 발사 반동 고리. 몸통은 Ev.Arrow kind 1.',
    layers: [
      L('projectile', 0, { y: 1.25, ahead: 0.6, length: 11, width: 0.75, tail: 3.0, life: 0.42, color: T, color2: PALE, intensity: 1.6, seed: 0.3 }),
      L('trail', 0, { variant: 2, y: 1.42, yaw: 0.03, ahead: 0.4, length: 5.5, width: 0.14, life: 0.24, tail: 0.14, color: S, color2: PALE, intensity: 2.0, seed: 0.6 }),
      L('trail', 0.03, { variant: 2, y: 1.08, yaw: -0.03, ahead: 0.4, length: 5.0, width: 0.13, life: 0.22, tail: 0.13, color: T, color2: PALE, intensity: 1.9, seed: 0.85 }),
      L('ring', 0, { length: 1.6, width: 0.26, life: 0.28, from: 0.25, color: DEEP, color2: T, intensity: 1.1, seed: 0.1 }),
    ],
  },
  // ── Lv1~25 필수 8종 ────────────────────────────
  {
    cue: 'lyra_triple', skillId: LyraSkill.Triple, live: true, reach: 0,
    note: '3연사: 세 갈래 바람 결(±7°) + 발밑 고리. 화살 3발은 Ev.Arrow.',
    layers: [
      L('trail', 0, { variant: 2, y: 1.25, ahead: 0.3, length: 2.0, width: 0.12, life: 0.15, tail: 0.1, color: T, color2: PALE, intensity: 1.9, seed: 0.15 }),
      L('trail', 0.05, { variant: 2, y: 1.25, yaw: 0.12, ahead: 0.3, length: 1.8, width: 0.11, life: 0.15, tail: 0.1, color: S, color2: PALE, intensity: 1.7, seed: 0.45 }),
      L('trail', 0.1, { variant: 2, y: 1.25, yaw: -0.12, ahead: 0.3, length: 1.8, width: 0.11, life: 0.15, tail: 0.1, color: T, color2: PALE, intensity: 1.7, seed: 0.75 }),
      L('ring', 0, { length: 1.0, width: 0.18, life: 0.24, from: 0.3, color: DEEP, color2: T, intensity: 0.9, seed: 0.3 }),
    ],
  },
  {
    cue: 'lyra_backstep', skillId: LyraSkill.Backstep, live: true, reach: 0,
    note: '백스텝 사격: 뒤로 빠지는 발밑 바람 먼지(연기 위주 폭발 플립북 작게 + 넓은 고리) + 뒤쪽으로 끌리는 바람 결 두 줄. 사격 화살은 Ev.Arrow.',
    layers: [
      L('blast', 0, { variant: 1, y: 0.35, length: 1.9, life: 0.5, color: S, color2: PALE, intensity: 1.0, seed: 0.55 }),
      L('ring', 0, { length: 2.0, width: 0.3, life: 0.32, from: 0.2, color: DEEP, color2: S, intensity: 1.0, seed: 0.65 }),
      L('trail', 0, { variant: 2, y: 0.55, yaw: Math.PI + 0.1, length: 2.6, width: 0.16, life: 0.22, tail: 0.15, color: T, color2: PALE, intensity: 1.8, seed: 0.25 }),
      L('trail', 0.03, { variant: 2, y: 1.1, yaw: Math.PI - 0.08, length: 2.2, width: 0.14, life: 0.2, tail: 0.14, color: S, color2: PALE, intensity: 1.6, seed: 0.9 }),
    ],
  },
  {
    cue: 'lyra_pierce', skillId: LyraSkill.Pierce, live: true, reach: 0,
    note: '관통 화살 발사: 길게 뻗는 청록 관통 심(14m) + 발사 반동 고리. 관통할 때마다 작은 고리는 LyraFx.pierceHit(맞은 자리).',
    layers: [
      L('projectile', 0, { y: 1.25, ahead: 0.6, length: 14, width: 0.5, tail: 3.6, life: 0.45, color: T, color2: PALE, intensity: 1.8, seed: 0.5 }),
      L('trail', 0, { variant: 2, y: 1.25, ahead: 0.3, length: 3.0, width: 0.16, life: 0.18, tail: 0.12, color: S, color2: PALE, intensity: 2.0, seed: 0.35 }),
      L('ring', 0, { length: 1.3, width: 0.22, life: 0.26, from: 0.3, color: DEEP, color2: T, intensity: 1.0, seed: 0.2 }),
    ],
  },
  {
    cue: 'lyra_launch_shot', skillId: LyraSkill.LaunchShot, live: true, reach: 3,
    note: '올려쏘기(Lv5 런처): 앞 3m 바닥에서 위로 솟는 가는 바람 기둥(띄움 높이) + 그 자리 고리 + 위로 휘는 은빛 호 한 줄.',
    layers: [
      L('pillar', 0.04, { length: 3.2, width: 0.32, life: 0.42, color: T, color2: PALE, intensity: 1.3, seed: 0.4 }),
      L('ring', 0, { length: 1.8, width: 0.3, life: 0.3, from: 0.3, color: DEEP, color2: T, intensity: 1.2, seed: 0.7 }),
      L('trail', 0, { variant: 1, y: 0.6, ahead: -1.6, roll: 1.35, length: 1.9, width: 0.22, start: -0.9, sweep: 1.9, life: 0.26, tail: 0.1, color: S, color2: PALE, intensity: 2.0, seed: 0.15 }),
    ],
  },
  {
    cue: 'lyra_rain_mark', skillId: LyraSkill.WindRain, live: true, reach: 5,
    note: '바람 화살비 표식: 앞 5m 지름 7m 청록 장판(1.6초) + 바깥에서 모여드는 고리. 낙하는 lyra_rain_tick.',
    layers: [
      L('zone', 0, { variant: 0, length: 3.5, life: 1.6, color: DEEP, color2: T, intensity: 1.3, seed: 0.3 }),
      L('ring', 0, { length: 3.5, from: 1.5, width: 0.22, life: 0.22, color: T, color2: PALE, intensity: 1.2, seed: 0.6 }),
    ],
  },
  {
    cue: 'lyra_rain_tick', skillId: LyraSkill.WindRain, live: true, reach: 5,
    note: '바람 화살비 낙하 한 번(판정 틱): 지름 7m 안에 화살 6발이 0.12초 동안 착탄 + 가운데 작은 바람 고리. 다단 틱마다 이 큐.',
    rain: { count: 6, radius: 3.2, delay: 0, spread: 0.12, height: 7, tilt: 0.2 },
    layers: [
      L('ring', 0.06, { length: 1.6, width: 0.2, life: 0.24, from: 0.35, color: DEEP, color2: T, intensity: 0.9, seed: 0.45 }),
    ],
  },
  {
    cue: 'lyra_air_volley', skillId: LyraSkill.AirVolley, live: true, reach: 0,
    note: '공중 연사(체공 사격): 몸 앞에서 아래로 펼쳐지는 부채꼴 바람 호 + 착탄 쪽 바닥 부채꼴 장판(0.5초). 화살은 Ev.Arrow kind 4(아래로 내리꽂힘).',
    layers: [
      L('arc', 0, { y: 1.6, ahead: 0.4, roll: -0.55, length: 2.2, width: 0.6, start: -0.75, sweep: 1.5, life: 0.24, color: T, color2: PALE, intensity: 1.7, seed: 0.35 }),
      L('zone', 0.05, { variant: 1, sweep: 1.2, ahead: 0.5, length: 5, life: 0.5, color: DEEP, color2: T, intensity: 1.2, seed: 0.6 }),
    ],
  },
  {
    cue: 'lyra_net_set', skillId: LyraSkill.NetTrap, live: true, reach: 2.5,
    note: '그물 덫 설치: 앞 2.5m 바닥 룬 원(장판 지름 3.2m) + 방사 룬 선(균열 방사) + 모여드는 고리.',
    layers: [
      L('zone', 0, { variant: 0, length: 1.6, life: 1.8, color: DEEP, color2: S, intensity: 1.2, seed: 0.8 }),
      L('crack', 0.02, { variant: 1, length: 1.5, life: 1.8, color: T, color2: S, intensity: 1.1, seed: 0.2 }),
      L('ring', 0, { length: 1.6, from: 1.8, width: 0.16, life: 0.2, color: T, color2: PALE, intensity: 1.2, seed: 0.1 }),
    ],
  },
  {
    cue: 'lyra_net_snap', skillId: LyraSkill.NetTrap, live: true, reach: 2.5,
    note: '그물 덫 발동(속박): 바깥에서 조여드는 고리 + 안으로 감기는 작은 소용돌이(그물 조임) + 은빛 가는 기둥.',
    layers: [
      L('ring', 0, { length: 0.4, from: 4.5, width: 0.2, life: 0.22, color: S, color2: PALE, intensity: 1.4, seed: 0.5 }),
      L('vortex', 0, { length: 1.8, from: 0.18, width: 0.2, sweep: 6, life: 0.55, color: T, color2: PALE, intensity: 1.6, seed: 0.3 }),
      L('pillar', 0.08, { length: 1.6, width: 0.5, life: 0.3, color: T, color2: PALE, intensity: 1.0, seed: 0.9 }),
    ],
  },
  {
    cue: 'lyra_explode', skillId: LyraSkill.Explode, live: true, reach: 7,
    note: '폭발 화살 착탄: 청록·은 폭발 플립북(지름 3.6m) + 충격파 고리 둘 + 방사 균열 + 귀퉁이 옅은 비네트(0.1, 섬광 아님). 넉백 광역.',
    layers: [
      L('blast', 0, { y: 1.0, length: 3.6, life: 0.6, color: T, color2: PALE, intensity: 1.6, seed: 0.25 }),
      L('ring', 0, { length: 4.0, width: 0.5, life: 0.4, from: 0.2, color: T, color2: PALE, intensity: 1.6, seed: 0.7 }),
      L('ring', 0.05, { length: 2.4, width: 0.32, life: 0.3, from: 0.3, color: DEEP, color2: S, intensity: 1.1, seed: 0.35 }),
      L('crack', 0, { variant: 1, length: 2.6, life: 0.9, color: T, color2: S, intensity: 1.2, seed: 0.55 }),
      L('screen', 0, { variant: 0, life: 0.25, color: T, intensity: 0.1 }),
    ],
  },
  // ── 가능하면 추가(Lv35·45·55) ────────────────────
  {
    cue: 'lyra_vortex', skillId: LyraSkill.Vortex, live: true, reach: 6,
    note: '회오리 화살(Lv35 흡입): 앞 6m 바깥 반경 3.2m 흡입 소용돌이(1.1초) + 퍼지는 얇은 고리.',
    layers: [
      L('vortex', 0, { length: 3.2, from: 0.22, width: 0.32, sweep: 7, life: 1.1, color: T, color2: PALE, intensity: 1.7, seed: 0.4 }),
      L('ring', 0, { length: 3.6, width: 0.16, life: 0.32, from: 0.4, color: DEEP, color2: T, intensity: 1.0, seed: 0.2 }),
      L('pillar', 0.1, { length: 2.4, width: 0.22, life: 0.7, color: T, color2: PALE, intensity: 0.9, seed: 0.65 }),
    ],
  },
  {
    cue: 'lyra_homing', skillId: LyraSkill.Homing, live: true, reach: 0,
    note: '유도 화살 폭풍(Lv45) 시전: 몸 둘레에서 솟는 은빛 고리·가는 기둥(화살이 떠오르는 순간). 유도 화살은 Ev.Arrow kind 5.',
    layers: [
      L('ring', 0, { length: 2.2, width: 0.26, life: 0.34, from: 0.25, color: T, color2: PALE, intensity: 1.2, seed: 0.3 }),
      L('pillar', 0, { length: 2.6, width: 0.45, life: 0.4, color: T, color2: PALE, intensity: 1.0, seed: 0.5 }),
      L('arc', 0.03, { y: 1.6, roll: 0.15, length: 1.4, width: 0.4, start: -2.2, sweep: 4.4, life: 0.3, color: S, color2: PALE, intensity: 1.5, seed: 0.8 }),
    ],
  },
  {
    cue: 'lyra_skyfall', skillId: LyraSkill.Skyfall, live: true, reach: 6,
    note: '천궁 강하(Lv55): 앞 6m 하늘에서 화살 14발 집중 낙하(0.2초) + 착탄 폭발 플립북 + 큰 충격파 + 방사 균열.',
    rain: { count: 14, radius: 2.0, delay: 0, spread: 0.2, height: 9, tilt: 0.12 },
    layers: [
      L('blast', 0.18, { y: 1.2, length: 4.2, life: 0.6, color: T, color2: PALE, intensity: 1.6, seed: 0.6 }),
      L('ring', 0.18, { length: 5.0, width: 0.55, life: 0.45, from: 0.2, color: T, color2: PALE, intensity: 1.6, seed: 0.2 }),
      L('crack', 0.18, { variant: 1, length: 3.2, life: 1.0, color: T, color2: S, intensity: 1.3, seed: 0.85 }),
      L('screen', 0.18, { variant: 0, life: 0.28, color: T, intensity: 0.12 }),
    ],
  },
  // ── 각성: 폭풍 화살비 ─────────────────────────────
  {
    cue: 'lyra_awaken_charge', skillId: LyraSkill.Awaken, live: true, reach: 0,
    note: '각성 집결: 영웅 둘레 반경 7m 넓은 표식(장판 2.2초) + 모여드는 고리 + 하늘로 솟는 청록 기둥. 섬광·화면 색은 기존 각성 흐름이 맡는다.',
    layers: [
      L('zone', 0, { variant: 0, length: 7, life: 2.2, color: DEEP, color2: T, intensity: 1.2, seed: 0.15 }),
      L('ring', 0, { length: 7, from: 1.4, width: 0.3, life: 0.3, color: T, color2: PALE, intensity: 1.3, seed: 0.5 }),
      L('pillar', 0, { length: 6, width: 0.6, life: 0.8, color: T, color2: PALE, intensity: 1.2, seed: 0.35 }),
    ],
  },
  {
    cue: 'lyra_awaken_rain', skillId: LyraSkill.Awaken, live: true, reach: 0,
    note: '각성 폭풍 화살비 한 파(다단 틱): 반경 7m 안에 화살 16발이 0.16초 동안 착탄 + 바깥으로 퍼지는 얇은 고리.',
    rain: { count: 16, radius: 6.5, delay: 0, spread: 0.16, height: 9, tilt: 0.16 },
    layers: [
      L('ring', 0.08, { length: 6.5, width: 0.2, life: 0.3, from: 0.5, color: DEEP, color2: T, intensity: 1.0, seed: 0.7 }),
    ],
  },
];

/** 큐 계약(common.md)의 vfx 이름 전부. 이 표가 모두 매핑한다(시험 고정) */
export const LYRA_VFX_CUES: readonly string[] = [
  'lyra_shot', 'lyra_shot_heavy', 'lyra_triple', 'lyra_backstep', 'lyra_pierce', 'lyra_launch_shot', 'lyra_rain_mark', 'lyra_rain_tick',
  'lyra_air_volley', 'lyra_net_set', 'lyra_net_snap', 'lyra_explode', 'lyra_vortex', 'lyra_homing', 'lyra_skyfall', 'lyra_awaken_charge', 'lyra_awaken_rain',
];

const BY_NAME = new Map<string, LyraCueEntry>();
for (const e of LYRA_CUE_MAP) BY_NAME.set(e.cue, e);

export function findLyraCue(name: string): LyraCueEntry | undefined {
  return BY_NAME.get(name);
}
